import { useLingui } from "@lingui/react/macro";
import { Button } from "@offstreetparking/design-system/forms";
import { LocationIcon } from "@offstreetparking/design-system/icons/LocationIcon";
import { QrCodeIcon } from "@offstreetparking/design-system/icons/QrCodeIcon";
import { Text } from "@offstreetparking/design-system/typography";
import { cleanPlate } from "@offstreetparking/domain/entities/vehicle";
import type { Permit } from "../types";
import { LazyQRCodeSVG } from "./LazyQRCodeSVG";
type ParcsCodePrintLayoutProps = {
	parcsCodes: Permit["parcsCodes"];
	publicIdentifier: Permit["publicIdentifier"];
};
export const ParcsCodePrintLayout = ({ parcsCodes, publicIdentifier }: ParcsCodePrintLayoutProps) => {
	const { t } = useLingui();
	const parcsCode = parcsCodes[0];
	const locationGroups = [
		...new Map(
			parcsCodes.map((pc) => {
				const groupLocationId = [...pc.locations.map((l) => l.id)].sort().join(",");
				return [
					groupLocationId,
					{
						groupLocationId,
						locationName: pc.locations.map((l) => l.name).join(", "),
					},
				];
			}),
		).values(),
	];
	return (
		<div className="hidden print:flex print:flex-col">
			<Text size="md">
				{t`Scan the QR code for your plate and location at the gate. Increase screen brightness for best results.`}
			</Text>

			{parcsCodes.length === 1 ? (
				<div className="flex justify-center">
					<div className="overflow-hidden rounded-md border-2 border-neutral-faded">
						<div className="bg-black p-3 text-center [print-color-adjust:exact]">
							<Text size="lg" weight="bold" className="text-white">
								{parcsCode?.plate}
							</Text>
							<div className="flex items-center justify-center gap-1">
								<LocationIcon size={12} className="text-white" />
								<Text size="xs" className="text-white">
									{parcsCode?.locations.map((l) => l.name).join(", ")}
								</Text>
							</div>
						</div>
						<div className="flex items-center justify-center bg-white p-6">
							<LazyQRCodeSVG size={180} value={parcsCode?.code ?? ""} level="H" />
						</div>
					</div>
				</div>
			) : (
				locationGroups.map(({ groupLocationId, locationName }) => {
					const items = parcsCodes.filter(
						(pc) => [...pc.locations.map((l) => l.id)].sort().join(",") === groupLocationId,
					);
					return (
						<div key={groupLocationId}>
							<div className="my-4">
								<Text size="xl" weight="bold">
									{locationName}
								</Text>
							</div>
							{items.map((item) => (
								<div key={item.code} className="mb-4">
									<div className="rounded-md border border-neutral-faded bg-white p-4">
										<div className="flex items-center">
											<QrCodeIcon size={32} className="text-neutral-faded" />
											<div className="flex-1 px-3">
												<Text size="md" weight="bold">
													{item.plate}
												</Text>
												<Text size="xs" weight="medium" intent="muted">
													{locationName}
												</Text>
											</div>
											<Button
												intent="primary"
												size="lg"
												href={`/confirmation/${publicIdentifier}?plate=${encodeURIComponent(cleanPlate(item.plate ?? ""))}`}
												className="whitespace-nowrap [print-color-adjust:exact]"
											>
												{t`Open QR Code`}
											</Button>
										</div>
									</div>
								</div>
							))}
						</div>
					);
				})
			)}
		</div>
	);
};
